import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { PhysiciansService } from "../../../../../../services/physician-service";
import { formatName } from "../../../../../../common/format-name";
import { formatPhoneNumber, formatZipCode } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { LineOfServices } from "../../../../../../enums/ReferralEnums/LineOfServiceEnum";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";
import { IValidateCustomElement } from "../../../../../../interfaces/i-validate-custom-element";
import { IGetPhysicianByName } from "../../../../../../resources-vue/vue-interfaces/i-get-physician";
import { IPhoneNumber } from "../../../../../../interfaces/i-phone-number";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import FormatName from "../../../../../../resources-vue/vue-custom-elements/FormatName/FormatName.vue";

export default defineComponent({
    inject: ["v"],
    components: {
        TypeaheadInput,
        FormatName
    },

    props: {
        providerId: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: "Physician"
        },
        physicianId: {
            type: String,
            default: ""
        },
        disabled: {
            type: Boolean,
            default: false
        },
        lineOfServiceValue: {
            type: Number,
            default: 0
        },
        accountId: {
            type: String,
            default: null
        }
    },

    emits: ["update:physicianId", "blurredInput"],

    data() {
        return {
            _physicianService: null as PhysiciansService,
            _ignoreIdChange: false as boolean,
            lineOfServiceEnumMap: LineOfServices,
            physicianDetails: null as any,
            loadingPhyDetails: false as boolean,
            physicianValidation: null as IValidateCustomElement,
            isValidationLoaded: false as boolean,
            isHospiceLos: false as boolean,
            primaryPhoneNumber: null as IPhoneNumber,
            alternatePhoneNumber: null as IPhoneNumber,
            pecosBadgeToolTip: "PECOS ineligibility may result in claim denial",
            physician: null as any,
            v$: this.v,
            formatZipCode,
            formatPhoneNumber
        };
    },

    watch: {
        physician(newValue) {
            this.physicianChanged(newValue);
        },
        physicianId(newValue) {
            this.physicianIdChanged(newValue);
        }
    },

    async created() {
        this._physicianService = Container.instance.get(PhysiciansService);
        this.isHospiceLos = this.lineOfServiceEnumMap.enum.Hospice == this.lineOfServiceValue;
    },

    async mounted() {
        if (this.physicianId) {
            await this.physicianIdChanged(this.physicianId);
        }
        if (this.isHospiceLos) {
            this.physicianValidation = {
                required: true,
                displayName: this.label
            };
        }
        this.isValidationLoaded = true;
    },

    computed: {
        hasData() {
            return this.physicianDetails !== null;
        }
    },

    methods: {
        emitBlur() {
            this.$emit("blurredInput");
        },

        async physicianChanged(phy: IGetPhysicianByName) {
            if (phy) {
                let attPhyId = phy.id;
                this._ignoreIdChange = true;
                this.$emit("update:physicianId", attPhyId);
                let npiIndex = phy?.name?.indexOf(" - NPI");
                if (npiIndex > 0 && phy?.name) {
                    let name = phy.name.slice(0, npiIndex + 1);
                    this.physician = { ...phy, name };
                } else {
                    this.physician = phy;
                }
                this.loadingPhyDetails = true;
                await this.loadPhysicianData(attPhyId);
                this.loadingPhyDetails = false;
            } else {
                this.physician = null;
                this.physicianDetails = null;
                this.loadingPhyDetails = false;
                this.$emit("update:physicianId", null);
            }
        },

        async physicianIdChanged(phy: string) {
            if (this.disabled) {
                this._ignoreIdChange = false;
            }
            if (this._ignoreIdChange) {
                this._ignoreIdChange = false;
                return;
            }
            if (phy && phy != "00000000-0000-0000-0000-000000000000") {
                this.loadingPhyDetails = true;
                await this.loadPhysicianData(phy);
                if (this.physicianDetails) {
                    this.physician = {
                        name: `${this.physicianDetails.lastName}, ${this.physicianDetails.firstName}`,
                        id: this.physicianDetails.id
                    };
                }
            } else {
                this.physician = null;
                this.physicianDetails = null;
                this.loadingPhyDetails = false;
            }
        },

        async getPhysicianList(filter: string, limit: number) {
            let physicians = await this._physicianService.getPhysiciansByName({
                name: filter,
                page: 1,
                pageLength: limit,
                providerIds: [this.providerId],
                accountId: this.accountId,
                application: ApplicationEnum.AxxessHospiceFE
            });
            let physiciansList = physicians.items?.map((physician: any) => {
                let fullName = this.splitName(physician.name);
                return {
                    id: physician.id,
                    name: formatName(fullName.firstName, fullName.lastName) + ` - NPI: ${physician.npi}`
                };
            });
            return physiciansList;
        },

        splitName(fullName: string) {
            let names = fullName.split(" ");
            return { firstName: names[0], lastName: names[1] };
        },

        async loadPhysicianData(attPhyId: string) {
            if (attPhyId && attPhyId != "00000000-0000-0000-0000-000000000000") {
                this.loadingPhyDetails = true;
                const payload = {
                    physicianId: attPhyId,
                    application: ApplicationEnum.AxxessHospiceFE,
                    accountId: this.accountId
                };
                this.physicianDetails = await this._physicianService.getPhysicianById(payload);
                this.loadingPhyDetails = false;
                // Array index for Primary and Alternate phone number is not always 0 and 1
                this.primaryPhoneNumber = this.physicianDetails?.phoneNumbers[0]?.isPrimary
                    ? this.physicianDetails.phoneNumbers[0]
                    : this.physicianDetails.phoneNumbers[1];
                this.alternatePhoneNumber = this.physicianDetails?.phoneNumbers[0]?.isPrimary
                    ? this.physicianDetails.phoneNumbers[1]
                    : this.physicianDetails.phoneNumbers[0];
            }
        }
    }
});
